/**
 * Client-side mirror of the server-side `ReCaptchaProtectedPathsFilter`
 * matching rules.
 *
 * `path` and every pattern in `patterns` are compared as the URI portion
 * AFTER `/api/{version}/openserviceproxy/` — the interceptor is responsible
 * for extracting that suffix. All comparisons are case-insensitive and
 * leading/trailing slashes are ignored.
 *
 * Pattern semantics:
 * - Plain-prefix entry (no `*`): matches when the path equals the entry OR
 *   continues past it on a `/` segment boundary. So `insurance/car` matches
 *   `insurance/car` and `insurance/car/quotes` but NOT `insurance/carrental`.
 * - Glob entry: `*` matches exactly one URI segment, `**` matches zero or
 *   more URI segments. The whole pattern is anchored (full-path match).
 *
 * @param path     Path suffix from the request URL, e.g. `insurance/car/quotes`.
 * @param patterns Patterns from `FrontendConfigurationModel.reCaptchaProtectedPaths`.
 * @returns `true` when any pattern matches; `false` when the list is empty or
 *          no pattern matches.
 */
export const isProtectedPath = (path: string, patterns: readonly string[]): boolean => {
	if (!patterns || patterns.length === 0) return false;
	const normalizedPath = normalize(path);
	return patterns.some((pattern) => matchPattern(normalizedPath, normalize(pattern)));
};

const normalize = (value: string): string => {
	// Hand-rolled trim rather than `.replace(/^\/+|\/+$/g, '')` because the
	// alternation-with-`+` pattern is flagged by SonarCloud rule S5852 for
	// super-linear backtracking on adversarial input. This two-pointer scan is
	// unambiguously O(n).
	let start = 0;
	let end = value.length;
	while (start < end && value.codePointAt(start) === 47 /* '/' */) start++;
	while (end > start && value.codePointAt(end - 1) === 47 /* '/' */) end--;
	return value.slice(start, end).toLowerCase();
};

const matchPattern = (path: string, pattern: string): boolean => {
	if (pattern === '') return false;

	if (!pattern.includes('*')) {
		if (path === pattern) return true;
		return path.startsWith(`${pattern}/`);
	}

	const pathSegments = path.split('/').filter((s) => s.length > 0);
	const patternSegments = pattern.split('/').filter((s) => s.length > 0);
	return matchSegments(pathSegments, 0, patternSegments, 0);
};

const matchSegments = (
	path: readonly string[],
	pathIndex: number,
	pattern: readonly string[],
	patternIndex: number
): boolean => {
	if (patternIndex === pattern.length) {
		return pathIndex === path.length;
	}

	const patternSegment = pattern[patternIndex];

	if (patternSegment === '**') {
		// `**` matches zero or more segments; try every possible consumption.
		for (let consume = 0; consume <= path.length - pathIndex; consume++) {
			if (matchSegments(path, pathIndex + consume, pattern, patternIndex + 1)) {
				return true;
			}
		}
		return false;
	}

	if (pathIndex === path.length) {
		return false;
	}

	if (patternSegment === '*') {
		return matchSegments(path, pathIndex + 1, pattern, patternIndex + 1);
	}

	if (patternSegment === path[pathIndex]) {
		return matchSegments(path, pathIndex + 1, pattern, patternIndex + 1);
	}

	return false;
};
